/**
 * Draft lesson description stores VIDEO entities as HTML. html-to-draftjs only
 * reads `src` on the <video> element (not nested <source>), so export/import
 * must keep the URL on <video src="..."> and hydrate legacy HTML from <source>.
 */

export const VIDEO_ENTITY_TYPE = 'VIDEO' as const;

export function buildVideoHtmlFromEntitySrc(src: string): string {
  const safe = src || '';
  return `<video controls style="width:100%;max-width:560px;border-radius:4px;background:#000" src="${safe}"><source src="${safe}" /></video>`;
}

export type VideoDraftChunk = {
  type: typeof VIDEO_ENTITY_TYPE;
  mutability: 'IMMUTABLE';
  data: { src: string };
};

export const IMAGE_ENTITY_TYPE = 'IMAGE' as const;

/**
 * Normalise a Draft IMAGE width/height value to a valid CSS size.
 *
 * html-to-draftjs only reads image dimensions back from the inline `style` and
 * only when they carry px units (or `auto`). Unitless numbers and width/height
 * attributes are dropped on reparse, which is why a resized image reverts to its
 * natural size. This keeps every serialization px-valid. Also collapses the
 * legacy `autopx` artifact (from appending `px` to `auto`) back to `auto`.
 */
export function toCssSize(value: unknown): string {
  if (value === undefined || value === null || value === '') return 'auto';
  if (typeof value === 'number') return Number.isFinite(value) ? `${value}px` : 'auto';
  const str = String(value).trim();
  if (str === '' || /^auto/.test(str)) return 'auto';
  if (/^\d+(\.\d+)?$/.test(str)) return `${str}px`;
  return str; // already carries a unit (e.g. "200px", "50%")
}

export type ImageAlignment = 'left' | 'center' | 'right';

/**
 * Coerce any stored alignment hint to one of the three supported values.
 * Legacy content only ever carried CSS `float` (left/right); `none`/empty → left.
 */
export function normalizeImageAlignment(value: unknown): ImageAlignment {
  const v = String(value ?? '')
    .trim()
    .toLowerCase();
  if (v === 'center' || v === 'centre') return 'center';
  if (v === 'right') return 'right';
  return 'left';
}

/** Inline CSS that positions a block-level image for a given alignment. */
function alignmentToCss(alignment: ImageAlignment): string {
  if (alignment === 'center') return 'margin: 16px auto; display: block;';
  if (alignment === 'right') return 'margin: 16px 0 16px auto; display: block;';
  return 'margin: 16px 0; display: block;'; // left / default
}

/** Serialize a Draft IMAGE entity to HTML with px-valid sizing + round-trippable alignment. */
export function buildImageHtmlFromEntity(data: any = {}): string {
  const width = toCssSize(data.width);
  const height = toCssSize(data.height);
  const src = data.src || '';
  const alt = data.alt || 'image';
  const alignment = normalizeImageAlignment(data.alignment);
  // `data-alignment` is the durable signal we read back in
  // getImageDraftChunkFromImageElement. We can't rely on CSS for the round-trip:
  // html-to-draftjs only restores image alignment from `float`, which has no
  // "center". The inline margin/display also positions the image in the
  // published (non-editor) lesson view.
  return `<img src="${src}" alt="${alt}" data-alignment="${alignment}" style="width: ${width}; height: ${height}; ${alignmentToCss(
    alignment
  )}"/>`;
}

export type ImageDraftChunk = {
  type: typeof IMAGE_ENTITY_TYPE;
  mutability: 'MUTABLE';
  data: { src: string; alt: string; width?: string; height?: string; alignment: ImageAlignment };
};

/**
 * Rebuild a Draft IMAGE entity (carrying alignment) from a parsed <img> DOM node.
 * Reads alignment from our `data-alignment` attribute, falling back to legacy
 * `float`. Without this the editor's default img handling drops the value and a
 * centred image reverts to the left on the next load.
 */
export function getImageDraftChunkFromImageElement(
  nodeName: string,
  node: HTMLElement
): ImageDraftChunk | null {
  if (nodeName !== 'img') return null;
  const img = node as HTMLImageElement;
  const src = (img.getAttribute('src') || img.src || '').trim();
  if (!src) return null;
  const alignmentHint = img.getAttribute('data-alignment') || img.style.float || '';
  return {
    type: IMAGE_ENTITY_TYPE,
    mutability: 'MUTABLE',
    data: {
      src,
      alt: img.getAttribute('alt') || 'image',
      width: img.style.width || undefined,
      height: img.style.height || undefined,
      alignment: normalizeImageAlignment(alignmentHint)
    }
  };
}

/** Combined custom chunk generator for the lesson editor: handles <video> and <img>. */
export function getMediaDraftChunk(
  nodeName: string,
  node: HTMLElement
): VideoDraftChunk | ImageDraftChunk | null {
  return (
    getVideoDraftChunkFromVideoElement(nodeName, node) ||
    getImageDraftChunkFromImageElement(nodeName, node)
  );
}

/** draftjs-to-html entity transformer for the lesson description editor. */
export function customEntityTransform(entity: any): string | undefined {
  if (entity.type === VIDEO_ENTITY_TYPE) {
    return buildVideoHtmlFromEntitySrc(entity.data?.src || '');
  }
  if (entity.type === IMAGE_ENTITY_TYPE) {
    return buildImageHtmlFromEntity(entity.data);
  }
  return undefined;
}

/**
 * Resolve video URL for Draft VIDEO entity from a parsed <video> DOM node.
 */
export function getVideoDraftChunkFromVideoElement(
  nodeName: string,
  node: HTMLElement
): VideoDraftChunk | null {
  if (nodeName !== 'video') return null;
  const video = node as HTMLVideoElement;
  let src = (video.getAttribute('src') || video.src || '').trim();
  if (!src) {
    const source = video.querySelector('source');
    src = (source?.getAttribute('src') || '').trim();
  }
  if (!src) return null;
  return {
    type: VIDEO_ENTITY_TYPE,
    mutability: 'IMMUTABLE',
    data: { src }
  };
}
